<script setup lang="ts">
  import { computed, nextTick, provide, ref, useId } from 'vue'
  import { onClickOutside } from '@vueuse/core'
  import { menuListboxKey } from '@/constants/menu'
  import type { HTMLAttributes } from 'vue'
  import DesMenuOptions from '@/components/menu/DesMenuOptions.vue'

  const props = defineProps<{
    label?: string
    bottom?: boolean
    right?: boolean
    left?: boolean
    inline?: boolean
    scroll?: boolean
    forceFlip?: boolean
    disabled?: boolean
    matchTrigger?: boolean
    listbox?: boolean
    controls?: string
    labelledBy?: string
    ariaLabel?: string
    testId?: string
  }>()

  const open = ref<boolean>(false)
  const trigger = ref<HTMLElement>()
  const toggleEl = ref<HTMLElement>()
  const container = ref<HTMLElement>()
  const options = ref<{ menuEl: HTMLElement | null }>()

  const listId = useId()

  const triggerAttrs = computed<HTMLAttributes & { 'data-testid'?: string }>(() => ({
    'role': props.listbox ? 'combobox' : 'button',
    'tabindex': props.disabled ? -1 : 0,
    'aria-haspopup': props.listbox ? 'listbox' : 'menu',
    'aria-expanded': open.value,
    'aria-controls': props.controls ?? listId,
    'aria-labelledby': props.labelledBy,
    'aria-label': props.labelledBy ? undefined : (props.ariaLabel ?? (props.label ? undefined : 'More options')),
    'aria-disabled': props.disabled || undefined,
    'data-testid': props.testId,
  }))

  provide(
    menuListboxKey,
    computed(() => (props.listbox ? { testId: props.testId } : null)),
  )

  defineExpose({
    open,
  })

  const handleEscapeKey = (e: KeyboardEvent) => e.key === 'Escape' && toggleMenu(false)
  const toggleMenu = (newState: boolean) => {
    if (props.disabled) return

    open.value = newState

    if (newState) {
      nextTick(() => options.value?.menuEl?.querySelector('input')?.focus())
      document.addEventListener('keydown', handleEscapeKey)
    } else {
      document.removeEventListener('keydown', handleEscapeKey)
    }
  }

  onClickOutside(trigger, (event: PointerEvent) => {
    if (event.target instanceof HTMLElement) {
      const ignoredElement = event.target.closest('[menu-toggle-ignore]')
      const current = event.target.closest('[data-e="menu"]')
      const siblingMenus = current?.parentElement
        ? [...current.parentElement.children].filter((c) => c.getAttribute('data-e') === 'menu' && c !== current)
        : []

      if (ignoredElement && container.value && !siblingMenus.includes(container.value)) return
    }
    toggleMenu(false)
  })
</script>

<template>
  <div class="select-none" :class="inline ? 'inline-flex' : 'block w-full'" ref="container" data-e="menu">
    <span @click.stop.prevent="toggleMenu(!open)" ref="trigger">
      <slot name="label"></slot>

      <span ref="toggleEl" class="block">
        <slot name="toggle">
          <div
            class="inset-0 flex w-full items-center justify-center text-black-10"
            :class="disabled ? 'cursor-not-allowed' : 'cursor-pointer'"
          >
            <span
              v-if="label"
              v-bind="triggerAttrs"
              @keydown.enter.prevent="toggleMenu(!open)"
              @keydown.space.prevent="toggleMenu(!open)"
              class="px-3 py-2 h-10 flex w-full gap-3 text-sm border leading-none transition-colors items-center"
              :class="{
                'border-blue-50': open,
                'border-black-90': !open && !disabled,
                'border-black-80 bg-black-98 text-black-40': disabled,
                'hover:border-black-60': !open && !inline && !disabled,
                'rounded-lg': inline,
                'rounded-sm': !inline,
              }"
            >
              <div class="grow py-1 whitespace-nowrap overflow-hidden text-ellipsis">{{ label }}</div>
              <des-icon icon="fa-light fa-chevron-down" :class="{ 'rotate-180': open }" class="transition-transform" />
            </span>
            <span
              v-else
              v-bind="triggerAttrs"
              @keydown.enter.prevent="toggleMenu(!open)"
              @keydown.space.prevent="toggleMenu(!open)"
              class="p-2 flex items-center justify-center hover:bg-white-gray rounded-lg"
              :class="{ 'bg-white-gray': open }"
            >
              <des-icon icon="fa-light fa-ellipsis-vertical" class="h-6 w-6"></des-icon>
            </span>
          </div>
        </slot>
      </span>
    </span>

    <DesMenuOptions
      :id="listId"
      ref="options"
      :trigger="toggleEl"
      :bottom="bottom"
      :left="left"
      :right="right"
      :open="open"
      :scroll="scroll"
      :force-flip="forceFlip"
      :match-trigger="matchTrigger"
    >
      <slot></slot>
    </DesMenuOptions>
  </div>
</template>
